import { useState } from "react";
import { HandCoins, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate, today } from "@/lib/api";
import { carLabel, can } from "@/lib/labels";
import { Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "h-10 bg-[#0F0F12] border-[#1A1A22]";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400";

function PayOwnerDialog({ open, onClose, car, sale, branches, user, onSaved }) {
  const visible = user?.role === "admin_cabang" ? branches.filter((b) => b.id === user.branch_id || b.is_pusat) : branches;
  const defKas = user?.role === "admin_cabang" ? user.branch_id : sale?.cash_branch_id || car.branch_id;
  const [form, setForm] = useState({ date: today(), cash_branch_id: defKas || "umum", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/cars/${car.id}/bayar-pemilik`, form);
      toast.success("Setoran ke pemilik dicatat ke Buku Kas");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Bayar ke Pemilik</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400 -mt-2">{carLabel(car)}{car.consign_owner ? ` · ${car.consign_owner}` : ""}</p>
        <div className="flex justify-between bg-[#0F0F12] border border-[#1A1A22] rounded-md p-3 text-sm">
          <span className="text-zinc-500">Jumlah Setoran (Harga Titip)</span>
          <Money value={car.consign_price} className="text-blue-500 font-semibold" />
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="pay-owner-form">
          <div className="space-y-2">
            <Label className={labelCls}>Tanggal</Label>
            <Input data-testid="pay-owner-date-input" type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Keluar dari Kas</Label>
            <Select value={form.cash_branch_id} onValueChange={(v) => setForm((f) => ({ ...f, cash_branch_id: v }))}>
              <SelectTrigger data-testid="pay-owner-branch-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                <SelectItem value="umum">Kas Umum (Tanpa Cabang)</SelectItem>
                {visible.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Keterangan</Label>
            <Input data-testid="pay-owner-desc-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Transfer BCA" className={inputCls} />
          </div>
          <Button data-testid="pay-owner-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan ke Buku Kas"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ConsignSection({ car, sale, branches, user, onSaved }) {
  const [open, setOpen] = useState(false);
  const paid = car.consign_paid;
  const cancel = async () => {
    if (!window.confirm("Batalkan setoran ke pemilik? Entri Buku Kas terkait akan dihapus.")) return;
    try {
      await api.delete(`/cars/${car.id}/bayar-pemilik`);
      toast.success("Setoran dibatalkan");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <div data-testid="consign-section" className="bg-[#0A0A0C] border border-emerald-500/20 rounded-md">
      <div className="flex items-center justify-between p-4 border-b border-[#1A1A22]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-500">Konsinyasi</p>
        {sale && !paid && can(user, "aksi_penjualan") && (
          <Button data-testid="pay-owner-button" size="sm" onClick={() => setOpen(true)} disabled={!car.consign_price} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold">
            <HandCoins className="w-3.5 h-3.5 mr-1" /> Bayar ke Pemilik
          </Button>
        )}
        {paid && user?.role === "admin" && (
          <Button data-testid="cancel-pay-owner-button" size="sm" variant="outline" onClick={cancel} className="border-rose-500/30 text-rose-500 hover:bg-rose-500/10">
            <Undo2 className="w-3.5 h-3.5 mr-1" /> Batalkan Setoran
          </Button>
        )}
      </div>
      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
        <div><p className="text-zinc-500 text-xs mb-1">Pemilik</p><p data-testid="consign-owner" className="text-zinc-200">{car.consign_owner || "-"}</p></div>
        <div><p className="text-zinc-500 text-xs mb-1">Harga Titip</p>
          {car.consign_price ? <Money value={car.consign_price} className="text-zinc-100" /> : <span data-testid="consign-price-empty" className="text-xs text-amber-500">Belum diisi — Edit Mobil</span>}
        </div>
        <div><p className="text-zinc-500 text-xs mb-1">Status Setoran</p>
          <p data-testid="consign-status" className={`text-sm font-medium ${paid ? "text-emerald-500" : sale ? "text-blue-500" : "text-zinc-400"}`}>
            {paid ? "Sudah Dibayar" : sale ? "Belum Dibayar" : "Unit belum terjual"}
          </p>
          {paid && <p className="text-xs text-zinc-500">{fmtDate(paid.date)}</p>}
        </div>
      </div>
      {open && <PayOwnerDialog open={open} onClose={() => setOpen(false)} car={car} sale={sale} branches={branches} user={user} onSaved={onSaved} />}
    </div>
  );
}
